<template>
  <div class="v-radar">
    <svg :width="svgSize" :height="svgSize" :viewBox="svgViewBox">
      <!-- extra white border -->
      <polygon
        v-if="extraBorder"
        :points="buildStructure(1.01)"
        :fill="transparent"
        stroke="white"
        :stroke-width="externalStrokeWidth"
      ></polygon>
      <!-- border/outermost gridline + background fill -->
      <polygon
        :points="buildStructure(1)"
        :fill="fillColor"
        :stroke="externalStrokeColor"
        :stroke-width="externalStrokeWidth"
      ></polygon>
      <!-- inner half background fill -->
      <polygon
        :points="buildStructure(0.5)"
        :fill="averageFillColor"
      ></polygon>
      <!-- colored filled area graphing the actual values -->
      <polygon :points="buildPoints" :fill="polyFillColor"></polygon>
      <!-- inner gridlines -->
      <polygon
        :points="buildStructure(n / (gridlineCount + 1))"
        :fill="transparent"
        :stroke="internalsStrokeColor"
        :stroke-width="internalsStrokeWidth"
        v-for="n in gridlineCount"
        :key="n"
      ></polygon>
      <!-- axes -->
      <line
        :x1="originPoint"
        :y1="originPoint"
        :x2="xLineCoordinate(index)"
        :y2="yLineCoordinate(index)"
        :stroke="linesStrokeColor"
        :stroke-width="linesStrokeWidth"
        v-for="index in statsLength"
        :key="index + 10"
      >
      </line>
      <!-- labels -->
      <g>
        <text
          :style="`fill: ${labelColor}`"
          :x="xTextCoordinate(index)"
          :y="yTextCoordinate(index)"
          font-family="Verdana"
          :font-size="labelSize"
          v-for="index in statsLength"
          :key="index"
          v-text="shortNames[index]"
        >
        </text>
      </g>
      <!-- circles marking vertices -->
      <g v-if="showVertices">
        <circle
          class="primary-color"
          :cx="graphPoint(index - 1).x"
          :cy="graphPoint(index - 1).y"
          r="18"
          stroke="white"
          stroke-width="5"
          v-for="index in statsLength"
          :key="index"
        >
        </circle>
      </g>
    </svg>
  </div>
</template>
<script>
/* eslint-disable */
export default {
  name: 'radar',
  props: {
    radar: {
      type: Object,
      required: false,
    },
    stats: Array,
    polycolor: String,
    gridlineCount: { type: Number, default: 9 },
    gridlineColor: { type: String, default: '#777777' },
    axesColor: { type: String, default: '#000000' },
    // background color for the actual graph (not the whole SVG view box)
    fillColor: { type: String, default: this.transparent },
    // inner half of the graph background can be filled a different color
    halfwayFillColor: { type: String, default: 'rgba(0, 0, 0, .2)' },
    // border around graph (not around whole SVG view box; i.e. the final gridline)
    borderWidth: { type: Number, default: 4 },
    borderColor: { type: String, default: '#222222' },
    extraBorder: { type: Boolean, default: false }, // extra thin white outline around graph
    showVertices: { type: Boolean, default: false },
    labelSize: { type: Number, default: 50 },
    startingAngleRadians: { type: Number, default: (Math.PI / 2)},
    maxValue: { type: Number, default: 100 },
    labelColor: { type: String, default: 'var(--primaryColor)' },
  },
  data() {
    return {
      transparent: 'rgba(0, 0, 0, 0)'
    };
  },
  methods: {
    buildStructure(percentage) {
      let points = '';
      for (let count = 0; count < this.statsLength; count++) {
        let newAngle = this.calculateNewAngle(count, this.baseAngle);
        let angleMultiplier = percentage * this.radius;
        let x = this.calculateXCoordinate(newAngle, angleMultiplier, this.originPoint);
        let y = this.calculateYCoordinate(newAngle, angleMultiplier, this.originPoint);
        points += Math.round(x) + ',' + Math.round(y) + ' ';
      }
      return points;
    },
    xLineCoordinate(index) {
      let newAngle = this.calculateNewAngle((index - 1), this.baseAngle);
      return this.calculateXCoordinate(newAngle, this.radius, this.originPoint);
    },
    yLineCoordinate(index) {
      let newAngle = this.calculateNewAngle((index - 1), this.baseAngle);
      return this.calculateYCoordinate(newAngle, this.radius, this.originPoint);
    },
    xTextCoordinate(index) {
      let newAngle = this.calculateNewAngle((index - 1), this.baseAngle);
      let x = 
        this.calculateXCoordinate(newAngle, parseFloat(this.radius) + 60, this.originPoint)
          + (Math.cos(newAngle) * this.shortNames[index].length * (this.labelSize / 50) * 11) // make room for name
          - 0.75 * (this.shortNames[index].length * (this.labelSize / 50) * 20); // center it
      return x;
    },
    yTextCoordinate(index) {
      let newAngle = this.calculateNewAngle((index - 1), this.baseAngle);
      return this.calculateYCoordinate(newAngle, parseFloat(this.radius) + 60, this.originPoint) + 20;
    },
    graphPoint(i) {
      let name = this.stats[i].name;
      let value = this.stats[i].value;
      let newAngle = this.calculateNewAngle(i, this.baseAngle);
      let angleMultiplier = value / this.maxValue * this.radius;
      let x = this.calculateXCoordinate(newAngle, angleMultiplier, this.originPoint);
      let y = this.calculateYCoordinate(newAngle, angleMultiplier, this.originPoint);
      // console.log(name);
      // console.log(value);
      // console.log({ x, y });
      return { x, y };
    },
    // @return the angle of one part based on total
    calculateBaseAngle(total) {
      return Math.PI * 2 / total;
    },
    // @return the angle of the index
    calculateNewAngle(index, baseAngle) {
      return this.startingAngleRadians + Math.PI + (baseAngle * index);
    },
    calculateXCoordinate(angle, angleMultiplier, originP) {
      return originP + Math.cos(angle) * angleMultiplier;
    },
    calculateYCoordinate(angle, angleMultiplier, originP) {
      return (originP + Math.sin(angle) * angleMultiplier);
    }
  },
  computed: {
    svgSize() {
      return setProperty(this.radar, 'size', '400') + 'px';
    },
    viewbox() {
      return setProperty(this.radar, 'viewbox', '1000');
    },
    svgViewBox() {
      return '0 0 ' + this.viewbox + ' ' + this.viewbox;
    },
    statsLength() {
      return this.stats.length;
    },
    baseAngle() {
      return this.calculateBaseAngle(this.statsLength);
    },
    radius() {
      return setProperty(this.radar, 'radius', '350');
    },
    originPoint() {
      return this.viewbox / 2;
    },
    externalStrokeColor() {
      if (objectIsDefined(this.radar)) {
        if (objectHasProperty(this.radar, 'structure')) {
          if (objectHasProperty(this.radar.structure, 'external')) {
            return setProperty(this.radar.structure.external, 'strokeColor', this.borderColor);
          }
        }
      }
      return this.borderColor;
    },
    externalStrokeWidth() {
      if (objectIsDefined(this.radar)) {
        if (objectHasProperty(this.radar, 'structure')) {
          if (objectHasProperty(this.radar.structure, 'external')) {
            return setProperty(this.radar.structure.external, 'strokeWidth', this.borderWidth) + 'px';
          }
        }
      }
      return this.borderWidth + 'px';
    },
    internalsStrokeColor() {
      if (objectIsDefined(this.radar)) {
        if (objectHasProperty(this.radar, 'structure')) {
          if (objectHasProperty(this.radar.structure, 'internals')) {
            return setProperty(this.radar.structure.internals, 'strokeColor', this.gridlineColor);
          }
        }
      }
      return this.gridlineColor;
    },
    internalsStrokeWidth() {
      if (objectIsDefined(this.radar)) {
        if (objectHasProperty(this.radar, 'structure')) {
          if (objectHasProperty(this.radar.structure, 'internals')) {
            return setProperty(this.radar.structure.internals, 'strokeWidth', '2') + 'px';
          }
        }
      }
      return '2px';
    },
    linesStrokeColor() {
      if (objectIsDefined(this.radar)) {
        if (objectHasProperty(this.radar, 'lines')) {
          return setProperty(this.radar.lines, 'strokeColor', this.axesColor);
        }
      }
      return this.axesColor;
    },
    linesStrokeWidth() {
      if (objectIsDefined(this.radar)) {
        if (objectHasProperty(this.radar, 'lines')) {
          return setProperty(this.radar.lines, 'strokeWidth', '2') + 'px';
        }
      }
      return '2px';
    },
    averageStrokeColor() {
      if (objectIsDefined(this.radar)) {
        if (objectHasProperty(this.radar, 'structure')) {
          if (objectHasProperty(this.radar.structure, 'average')) {
            return setProperty(this.radar.structure.average, 'strokeColor', this.gridlineColor);
          }
        }
      }
      return this.gridlineColor;
    },
    averageStrokeWidth() {
      if (objectIsDefined(this.radar)) {
        if (objectHasProperty(this.radar, 'structure')) {
          if (objectHasProperty(this.radar.structure, 'average')) {
            return setProperty(this.radar.structure.average, 'strokeWidth', '2') + 'px';
          }
        }
      }
      return '2px';
    },
    averageFillColor() {
      if (objectIsDefined(this.radar)) {
        if (objectHasProperty(this.radar, 'structure')) {
          if (objectHasProperty(this.radar.structure, 'average')) {
            return setProperty(this.radar.structure.average, 'fillColor', this.halfwayFillColor);
          }
        }
      }
      return this.halfwayFillColor;
    },
    buildPoints() {
      let points = '';
      for (let i = 0; i < this.statsLength; i++) {
        let graphPoint = this.graphPoint(i);
        points += Math.round(graphPoint.x) + ',' + Math.round(graphPoint.y) + ' ';
      }
      return points;
    },
    shortNames() {
      let shortNames = [0];
      for (let i = 0; i < this.statsLength; i++) {
        let stat = this.stats[i];
        if (objectHasProperty(stat, 'shortName')) { // eslint-disable-line
          shortNames.push(setProperty(stat, 'shortName', stat.name)); // eslint-disable-line
        } else {
          shortNames.push(stat.name);
        }
      }
      return shortNames;
    },
    polyFillColor() {
      if (this.polycolor === '') {
        return 'rgba(200, 50, 150, .7)';
      } else { // eslint-disable-line
        return this.polycolor;
      }
    }
  }
};

// TODO: all this nonsense with strings and reflection just to do undefined checks should absolutely be refactored out.

function objectIsDefined(object) {
  return (typeof object !== 'undefined');
}
function objectHasProperty(object, property) {
  if (typeof object !== 'undefined') {
    if (object.hasOwnProperty(property)) { // eslint-disable-line
      return true;
    }
  }
  return false;
}
function setProperty(object, propertyToTest, defaultValue) {
  if (objectHasProperty(object, propertyToTest)) {
    if (typeof object[propertyToTest] !== 'undefined') {
      return object[propertyToTest];
    }
  }
  return defaultValue;
}
// @return the obj length
function objectLength(obj) {
  let i; // eslint-disable-line no-unused-vars
  let length = 0; // eslint-disable-line no-shadow
  for (i in obj) { // eslint-disable-line guard-for-in, no-restricted-syntax
    length += 1;
  }
  return length;
}

</script>
<style lang="scss" scoped>
.primary-color {
  fill: var(--primaryColor);
}
</style>
/* eslint-enable */



// WEBPACK FOOTER //
// src/components/game/ui/Radar.vue